<template>
  <div class="categories-page">
    <header class="categories-page__header">
      <div>
        <h1>{{ $t('catalog.categories.title') }}</h1>
        <p>{{ $t('catalog.categories.subtitle') }}</p>
      </div>
      <button type="button" class="categories-page__btn" @click="openCreate">
        <i class="mdi mdi-plus"></i>
        {{ $t('catalog.categories.add') }}
      </button>
    </header>

    <div v-if="pagedCategories.length" class="categories-page__grid">
      <article v-for="category in pagedCategories" :key="category.id" class="category-card">
        <div class="category-card__top">
          <div class="category-card__icon">
            <i class="mdi mdi-folder-outline"></i>
          </div>
          <div class="category-card__actions">
            <button
              type="button"
              class="category-card__icon-btn"
              :title="$t('catalog.common.edit')"
              @click="openEdit(category)"
            >
              <i class="mdi mdi-pencil-outline"></i>
            </button>
            <button
              type="button"
              class="category-card__icon-btn category-card__icon-btn--danger"
              :title="$t('catalog.common.delete')"
              @click="askDelete(category)"
            >
              <i class="mdi mdi-trash-can-outline"></i>
            </button>
          </div>
        </div>
        <h3>{{ category.name }}</h3>
      </article>
    </div>

    <div v-else class="categories-page__empty">
      <i class="mdi mdi-folder-plus-outline"></i>
      <p>{{ $t('catalog.categories.empty') }}</p>
      <span>{{ $t('catalog.categories.emptyHint') }}</span>
    </div>

    <div v-if="pageCount > 1" class="categories-page__pagination">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="5"
        color="#0b4f8a"
      />
    </div>

    <v-dialog v-model="formOpen" max-width="440" content-class="category-dialog-wrap">
      <div class="category-modal">
        <header class="category-modal__header">
          <div class="category-modal__badge">
            <i :class="['mdi', isEdit ? 'mdi-pencil-outline' : 'mdi-folder-plus-outline']"></i>
          </div>
          <div>
            <h2>{{ isEdit ? $t('catalog.categories.editTitle') : $t('catalog.categories.createTitle') }}</h2>
            <p>{{ isEdit ? $t('catalog.categories.editHint') : $t('catalog.categories.createHint') }}</p>
          </div>
          <button type="button" class="category-modal__close" @click="formOpen = false">
            <i class="mdi mdi-close"></i>
          </button>
        </header>

        <div class="category-modal__body">
          <label class="category-modal__field">
            <span>{{ $t('catalog.common.name') }}</span>
            <input
              v-model.trim="name"
              type="text"
              :placeholder="$t('catalog.categories.namePlaceholder')"
              maxlength="255"
              @keyup.enter="save"
            >
          </label>
        </div>

        <footer class="category-modal__footer">
          <button type="button" class="category-modal__btn category-modal__btn--ghost" @click="formOpen = false">
            {{ $t('catalog.common.cancel') }}
          </button>
          <button
            type="button"
            class="category-modal__btn"
            :disabled="saving || !name"
            @click="save"
          >
            <i v-if="saving" class="mdi mdi-loading mdi-spin"></i>
            <span>{{ saving ? $t('catalog.common.saving') : $t('catalog.common.save') }}</span>
          </button>
        </footer>
      </div>
    </v-dialog>

    <v-dialog v-model="deleteOpen" max-width="420" content-class="category-dialog-wrap">
      <div class="category-modal category-modal--danger">
        <header class="category-modal__header">
          <div class="category-modal__badge category-modal__badge--danger">
            <i class="mdi mdi-trash-can-outline"></i>
          </div>
          <div>
            <h2>{{ $t('catalog.categories.deleteTitle') }}</h2>
            <p>{{ $t('catalog.categories.deleteWarning') }}</p>
          </div>
          <button type="button" class="category-modal__close" @click="deleteOpen = false">
            <i class="mdi mdi-close"></i>
          </button>
        </header>

        <div class="category-modal__body">
          <p class="category-modal__warning">
            {{ $t('catalog.categories.deleteDescription', { name: deleteName }) }}
          </p>
        </div>

        <footer class="category-modal__footer">
          <button type="button" class="category-modal__btn category-modal__btn--ghost" @click="deleteOpen = false">
            {{ $t('catalog.common.cancel') }}
          </button>
          <button
            type="button"
            class="category-modal__btn category-modal__btn--danger"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <i v-if="deleting" class="mdi mdi-loading mdi-spin"></i>
            <span>{{ deleting ? $t('catalog.common.deleting') : $t('catalog.common.delete') }}</span>
          </button>
        </footer>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { createCategory, deleteCategory, getCategories, updateCategory } from '@/api/categories'
import { notify, notifyError } from '@/utils/notify'

const PER_PAGE = 12

export default {
  name: 'CategoryControl',
  data() {
    return {
      categories: [],
      page: 1,
      formOpen: false,
      isEdit: false,
      categoryId: null,
      name: '',
      saving: false,
      deleteOpen: false,
      deleteName: '',
      deleting: false,
    }
  },
  computed: {
    pageCount() {
      return Math.ceil(this.categories.length / PER_PAGE) || 0
    },
    pagedCategories() {
      const start = (this.page - 1) * PER_PAGE
      return this.categories.slice(start, start + PER_PAGE)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      try {
        this.categories = await getCategories()
        if (this.page > this.pageCount && this.pageCount > 0) {
          this.page = this.pageCount
        }
      } catch (error) {
        notifyError(error)
      }
    },
    openCreate() {
      this.isEdit = false
      this.categoryId = null
      this.name = ''
      this.formOpen = true
    },
    openEdit(category) {
      this.isEdit = true
      this.categoryId = category.id
      this.name = category.name
      this.formOpen = true
    },
    askDelete(category) {
      this.categoryId = category.id
      this.deleteName = category.name
      this.deleteOpen = true
    },
    async confirmDelete() {
      this.deleting = true
      try {
        await deleteCategory(this.categoryId)
        notify(this.$t('catalog.categories.deleted'), 'warning')
        this.deleteOpen = false
        await this.load()
      } catch (error) {
        notifyError(error, this.$t('catalog.categories.deleteFailed'))
      } finally {
        this.deleting = false
      }
    },
    async save() {
      if (!this.name) {
        notify(this.$t('catalog.categories.enterName'), 'warning')
        return
      }

      this.saving = true
      try {
        const result = this.isEdit
          ? await updateCategory(this.categoryId, { name: this.name })
          : await createCategory({ name: this.name })

        notify(result.message || this.$t('catalog.common.saved'))
        this.formOpen = false
        await this.load()
      } catch (error) {
        notifyError(error)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.categories-page {
  padding: 28px 32px 40px;
  color: #152033;
}

.categories-page__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: #6b7a90;
    font-size: 14px;
  }
}

.categories-page__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border: 0;
  border-radius: 12px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 650;
  cursor: pointer;
  transition: background 0.2s ease;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    background: #093f6d;
  }
}

.categories-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
}

.category-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  transition: border-color 0.2s ease, transform 0.15s ease;

  &:hover {
    border-color: #b7c6d6;
    transform: translateY(-2px);
  }

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.35;
  }
}

.category-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.category-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: #0b4f8a;

  .mdi {
    font-size: 22px;
  }
}

.category-card__actions {
  display: flex;
  gap: 6px;
}

.category-card__icon-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #d9e2ec;
  border-radius: 10px;
  background: #fff;
  color: #3d4f63;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.15s ease;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    border-color: #0b4f8a;
    color: #0b4f8a;
    background: #eef4fa;
  }

  &--danger {
    color: #b42318;

    &:hover {
      border-color: #f0b4ae;
      color: #b42318;
      background: #fff5f4;
    }
  }
}

.categories-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 240px;
  padding: 48px;
  text-align: center;
  color: #6b7a90;
  border: 1px dashed #d9e2ec;
  border-radius: 16px;
  background: #fff;

  .mdi {
    font-size: 40px;
    color: #9eb6cc;
  }

  p {
    margin: 0;
    color: #152033;
    font-size: 16px;
    font-weight: 700;
  }

  span {
    font-size: 13px;
  }
}

.categories-page__pagination {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

.category-modal {
  overflow: hidden;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(18, 32, 51, 0.18);
}

.category-modal__header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 20px 0;
}

.category-modal__badge {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: #0b4f8a;

  .mdi {
    font-size: 22px;
  }

  &--danger {
    background: #fff1f0;
    color: #b42318;
  }
}

.category-modal__header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 750;
  letter-spacing: -0.02em;
  color: #152033;
}

.category-modal__header p {
  margin: 4px 0 0;
  color: #6b7a90;
  font-size: 13px;
  line-height: 1.4;
}

.category-modal__close {
  margin-left: auto;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: #f3f6fa;
  color: #6b7a90;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    background: #e8eef5;
    color: #152033;
  }
}

.category-modal__body {
  padding: 20px;
}

.category-modal__field {
  display: block;

  > span {
    display: block;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 700;
    color: #152033;
  }

  input {
    width: 100%;
    padding: 14px 16px;
    border: 1.5px solid #d9e2ec;
    border-radius: 14px;
    background: #fbfdff;
    color: #152033;
    font-size: 15px;
    font-weight: 600;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &::placeholder {
      color: #9aa8b8;
      font-weight: 500;
    }

    &:focus {
      border-color: #0b4f8a;
      box-shadow: 0 0 0 4px rgba(11, 79, 138, 0.1);
    }
  }
}

.category-modal__warning {
  margin: 0;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fff7f6;
  border: 1px solid #f5d5d1;
  color: #5b3a38;
  font-size: 14px;
  line-height: 1.45;

  strong {
    color: #152033;
  }
}

.category-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 20px 20px;
}

.category-modal__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 120px;
  padding: 12px 16px;
  border: 0;
  border-radius: 12px;
  background: #0b4f8a;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, opacity 0.15s ease;

  .mdi {
    font-size: 16px;
  }

  &:hover:not(:disabled) {
    background: #093f6d;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &--ghost {
    background: #f3f6fa;
    color: #3d4f63;

    &:hover:not(:disabled) {
      background: #e8eef5;
    }
  }

  &--danger {
    background: #b42318;

    &:hover:not(:disabled) {
      background: #912018;
    }
  }
}

@media (max-width: 720px) {
  .categories-page {
    padding: 20px 16px 32px;
  }

  .categories-page__header {
    flex-direction: column;
  }

  .category-modal__footer {
    flex-direction: column-reverse;

    .category-modal__btn {
      width: 100%;
    }
  }
}
</style>

<style lang="scss">
.category-dialog-wrap {
  box-shadow: none !important;
  background: transparent !important;
  overflow: visible !important;
}
</style>
